import { useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { ItemCategory, SubCategory } from "../api/types";
import { Modal } from "./Modal";

interface Props {
  /** Called after the modal is dismissed. */
  onClose: () => void;
  /** Called whenever the taxonomy changes so the parent can refresh its lists. */
  onChanged: () => void;
}

/**
 * Owner/Manager tool to create, rename, and delete the controlled flower-type
 * taxonomy (ItemSubCategory) used by the catalogue and the bouquet builder.
 */
export function ManageFlowerTypesModal({ onClose, onChanged }: Props) {
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newCategoryId, setNewCategoryId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const reloadSubs = async () => {
    setSubs(await api.listSubCategories());
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.listCategories(), api.listSubCategories()])
      .then(([cats, subRows]) => {
        if (cancelled) return;
        setCategories(cats);
        setSubs(subRows);
        const flower = cats.find((c) => c.code === "FLOWER");
        setNewCategoryId(flower?.id ?? cats[0]?.id ?? "");
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Could not load flower types");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Subcategories grouped by parent category, with a stable label lookup.
  const groups = useMemo(() => {
    const byCat = new Map<string, SubCategory[]>();
    for (const s of subs) {
      const key = s.categoryId ?? "";
      const arr = byCat.get(key) ?? [];
      arr.push(s);
      byCat.set(key, arr);
    }
    return [...byCat.entries()]
      .map(([categoryId, rows]) => ({
        categoryId,
        label: categories.find((c) => c.id === categoryId)?.label ?? "Other",
        rows: [...rows].sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [subs, categories]);

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!newCategoryId || !label) return;
    setCreating(true);
    setError(null);
    try {
      await api.createSubCategory({ categoryId: newCategoryId, label });
      setNewLabel("");
      await reloadSubs();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not create flower type");
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (id: string) => {
    const label = editLabel.trim();
    if (!label) return;
    setBusyId(id);
    setError(null);
    try {
      await api.updateSubCategory(id, { label });
      setEditingId(null);
      setEditLabel("");
      await reloadSubs();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not rename flower type");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (s: SubCategory) => {
    if (
      !window.confirm(
        `Delete flower type "${s.label}"? Items using it will be left untagged. ` +
          "(A type used in a bouquet recipe cannot be deleted.)",
      )
    ) {
      return;
    }
    setBusyId(s.id);
    setError(null);
    try {
      await api.deleteSubCategory(s.id);
      await reloadSubs();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not delete flower type");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal title="Manage flower types" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Flower types (e.g. Roses, Peonies) group items so the bouquet builder
          can filter by them. Tag any rose variety as “Roses” to keep them
          together.
        </p>

        {error ? <div className="alert error">{error}</div> : null}

        {/* Create */}
        <div
          style={{
            display: "flex",
            gap: "var(--space-2)",
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div className="field" style={{ margin: 0, minWidth: 140 }}>
            <label htmlFor="new-sub-category">Category</label>
            <select
              id="new-sub-category"
              value={newCategoryId}
              onChange={(e) => setNewCategoryId(e.target.value)}
              disabled={loading || creating}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ margin: 0, flex: 1, minWidth: 160 }}>
            <label htmlFor="new-sub-label">New type name</label>
            <input
              id="new-sub-label"
              type="text"
              value={newLabel}
              placeholder="e.g. Spray Roses"
              maxLength={120}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
              disabled={loading || creating}
            />
          </div>
          <button
            type="button"
            className="button"
            disabled={loading || creating || !newCategoryId || !newLabel.trim()}
            onClick={() => void handleCreate()}
          >
            {creating ? "Adding…" : "Add type"}
          </button>
        </div>

        {/* List */}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="muted">No flower types yet — add one above.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {groups.map((g) => (
              <div key={g.categoryId || "none"}>
                <div
                  className="muted"
                  style={{ fontSize: 12, textTransform: "uppercase", marginBottom: 4 }}
                >
                  {g.label}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  {g.rows.map((s) => {
                    const editing = editingId === s.id;
                    const busy = busyId === s.id;
                    return (
                      <div
                        key={s.id}
                        style={{
                          display: "flex",
                          gap: "var(--space-2)",
                          alignItems: "center",
                        }}
                      >
                        {editing ? (
                          <>
                            <input
                              type="text"
                              value={editLabel}
                              maxLength={120}
                              autoFocus
                              onChange={(e) => setEditLabel(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") void handleRename(s.id);
                                if (e.key === "Escape") setEditingId(null);
                              }}
                              style={{ flex: 1 }}
                              disabled={busy}
                            />
                            <button
                              type="button"
                              className="button"
                              disabled={busy || !editLabel.trim()}
                              onClick={() => void handleRename(s.id)}
                            >
                              {busy ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              className="button plain"
                              disabled={busy}
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={{ flex: 1 }}>{s.label}</span>
                            <button
                              type="button"
                              className="button tinted"
                              disabled={busy}
                              onClick={() => {
                                setEditingId(s.id);
                                setEditLabel(s.label);
                                setError(null);
                              }}
                            >
                              Rename
                            </button>
                            <button
                              type="button"
                              className="button danger"
                              disabled={busy}
                              onClick={() => void handleDelete(s)}
                            >
                              {busy ? "…" : "Delete"}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
